import React from 'react'
import { observer } from 'mobx-react'
import { Button, Checkbox, Form, Header, Modal, Select } from 'semantic-ui-react'

// Extended dating profile attributes (classifiers). Keys match the snake_case the OC bot prompt expects.
const CLASSIFIER_FIELDS = [
  { key: 'looking_for', label: 'Looking for' },
  { key: 'marital_status', label: 'Marital status' },
  { key: 'race', label: 'Race' },
  { key: 'height', label: 'Height' },
  { key: 'body_type', label: 'Body type' },
  { key: 'hair_color', label: 'Hair color' },
  { key: 'eye_color', label: 'Eye color' },
  { key: 'religion', label: 'Religion' },
  { key: 'smoking', label: 'Smoking' },
  { key: 'drinking', label: 'Drinking' },
  { key: 'education', label: 'Education' },
  { key: 'profession', label: 'Profession' },
  { key: 'looking_for_what', label: 'Looking for what' },
  { key: 'position', label: 'Position' },
  { key: 'exercise', label: 'Exercise' },
  { key: 'pets', label: 'Pets' },
  { key: 'piercings', label: 'Piercings' },
  { key: 'tattoos', label: 'Tattoos' },
  { key: 'sex_spot', label: 'Sex spot' },
  { key: 'my_characteristics', label: 'My characteristics' },
  { key: 'favorite_activities', label: 'Favorite activities' },
  { key: 'what_turns_me_on', label: 'What turns me on' },
  { key: 'arrangement_type', label: 'Arrangement type' },
  { key: 'children', label: 'Children' },
  { key: 'budget', label: 'Budget' },
  { key: 'income', label: 'Income' },
  { key: 'net_worth', label: 'Net worth' },
]

const GENDER_OPTIONS = [
  { key: '', value: '', text: '-' },
  { key: 'Man', value: 'Man', text: 'Man' },
  { key: 'Woman', value: 'Woman', text: 'Woman' },
]

const emptyClassifiers = () => CLASSIFIER_FIELDS.reduce((acc, field) => ({ ...acc, [field.key]: '' }), {})

const DEFAULT_STATE = {
  isOpen: false,
  loading: false,
  error: null,
  whiteLabelUid: '',
  isPremium: false,
  followUp: false,
  message: '',
  user0Username: '',
  user0Location: '',
  user0Age: '',
  user0Description: '',
  user0Gender: '',
  user0ProfileImgs: '',
  user0Classifiers: emptyClassifiers(),
  user1Username: '',
  user1Location: '',
  user1Age: '',
  user1Description: '',
  user1Gender: '',
  user1ProfileImgs: '',
  user1Classifiers: emptyClassifiers(),
  user1BillingCycle: '',
  user1RemainingCredits: '',
}

class OcBotTestCreateModal extends React.Component {

  state = { ...DEFAULT_STATE }

  handleOpen = () => this.setState({ ...DEFAULT_STATE, user0Classifiers: emptyClassifiers(), user1Classifiers: emptyClassifiers(), isOpen: true })
  handleClose = () => this.setState({ isOpen: false })

  onChange = (e, { name, value, checked }) => {
    this.setState({ [name]: value !== undefined ? value : checked })
  }

  onClassifierChange = (profile, key, value) => {
    const stateKey = `${profile}Classifiers`
    this.setState((prev) => ({ [stateKey]: { ...prev[stateKey], [key]: value } }))
  }

  parseImgs = (raw) => raw.split('\n').map((url) => url.trim()).filter((url) => url.length > 0)

  buildProfile = (profile) => {
    const built = {
      username: this.state[`${profile}Username`],
      location: this.state[`${profile}Location`],
      age: Number(this.state[`${profile}Age`]),
      description: this.state[`${profile}Description`],
    }

    const gender = this.state[`${profile}Gender`]
    if(gender) {
      built.gender = gender
    }

    const classifiers = {}
    Object.entries(this.state[`${profile}Classifiers`]).forEach(([key, value]) => {
      if(value !== '') {
        classifiers[key] = value
      }
    })
    if(Object.keys(classifiers).length > 0) {
      built.classifiers = classifiers
    }

    const imgs = this.parseImgs(this.state[`${profile}ProfileImgs`])
    if(imgs.length > 0) {
      built.profileImgs = imgs
    }

    return built
  }

  submit = async() => {
    const { whiteLabelUid, isPremium, followUp, message, user0Username, user0Location, user0Age, user0Description, user1Username, user1Location, user1Age, user1Description, user1BillingCycle, user1RemainingCredits } = this.state

    if(!whiteLabelUid || !user0Username || !user0Location || !user0Age || !user0Description || !user1Username || !user1Location || !user1Age || !user1Description) {
      this.setState({ error: 'Please fill in all required fields' })
      return
    }

    this.setState({ loading: true, error: null })

    const user1Profile = this.buildProfile('user1')
    if(user1BillingCycle !== '') {
      user1Profile.billingCycle = Number(user1BillingCycle)
    }
    if(user1RemainingCredits !== '') {
      user1Profile.remainingCredits = Number(user1RemainingCredits)
    }

    const conversationInfo = {
      whiteLabelUid,
      isPremium,
      user0Profile: this.buildProfile('user0'),
      user1Profile,
    }

    const [result, error] = await this.props.actions.admin.createOcBotTestConversation(conversationInfo)

    if(error) {
      this.setState({ loading: false, error: 'Could not create the conversation' })
      return
    }

    // Best-effort: the conversation is already created, so navigate to it regardless of
    // whether the immediate bot reply succeeds - the admin can retry sending it from there.
    // With no agentMessage, the bot just opens the conversation on its own.
    await this.props.actions.admin.generateOcBotTestResponse({ conversationId: result.id, agentMessage: followUp ? undefined : (message || undefined), isFollowup: followUp || undefined })

    this.setState({ loading: false, isOpen: false })
    this.props.onCreated(result.id)
  }

  renderProfileExtras = (profile, showBilling) => {
    const gender = this.state[`${profile}Gender`]
    const classifiers = this.state[`${profile}Classifiers`]
    const profileImgs = this.state[`${profile}ProfileImgs`]

    // Render the classifiers 3 per row to keep the (optional) form compact.
    const rows = []
    for(let i = 0; i < CLASSIFIER_FIELDS.length; i += 3) {
      rows.push(CLASSIFIER_FIELDS.slice(i, i + 3))
    }

    return (
      <>
        <Form.Group widths="equal">
          <Form.Field>
            <label>Gender</label>
            <Select
              placeholder="-"
              options={GENDER_OPTIONS}
              value={gender}
              onChange={(e, data) => this.onChange(e, { name: `${profile}Gender`, value: data.value })}
            />
          </Form.Field>
          {showBilling ? (
            <>
              <Form.Field>
                <label>Billing cycle (successful transactions)</label>
                <input type="number" value={this.state.user1BillingCycle} onChange={(e) => this.onChange(e, { name: 'user1BillingCycle', value: e.target.value })}/>
              </Form.Field>
              <Form.Field>
                <label>Remaining credits</label>
                <input type="number" value={this.state.user1RemainingCredits} onChange={(e) => this.onChange(e, { name: 'user1RemainingCredits', value: e.target.value })}/>
              </Form.Field>
            </>
          ) : <Form.Field/>}
        </Form.Group>

        <Form.Field>
          <label>Profile image URLs (one per line)</label>
          <textarea rows={2} value={profileImgs} onChange={(e) => this.onChange(e, { name: `${profile}ProfileImgs`, value: e.target.value })}/>
        </Form.Field>

        <Header as="h5">Profile fields (optional)</Header>
        {rows.map((row, i) => (
          <Form.Group widths="equal" key={i}>
            {row.map((field) => (
              <Form.Field key={field.key}>
                <label>{field.label}</label>
                <input value={classifiers[field.key]} onChange={(e) => this.onClassifierChange(profile, field.key, e.target.value)}/>
              </Form.Field>
            ))}
            {row.length < 3 && Array.from({ length: 3 - row.length }).map((_, k) => <Form.Field key={`pad-${k}`}/>)}
          </Form.Group>
        ))}
      </>
    )
  }

  render() {
    const { loading, error, whiteLabelUid, isPremium, followUp, message, user0Username, user0Location, user0Age, user0Description, user1Username, user1Location, user1Age, user1Description } = this.state
    const whiteLabelOptions = (this.props.store.whiteLabels || []).map(whiteLabel => ({
      key: whiteLabel.whiteLabelUid,
      value: whiteLabel.whiteLabelUid,
      text: whiteLabel.displayName || whiteLabel.siteName,
    }))

    return (
      <Modal open={this.state.isOpen} onClose={this.handleClose} trigger={
        <Button primary onClick={this.handleOpen}>Create new conversation</Button>
      }>
        <Modal.Header>Create OC bot test conversation</Modal.Header>
        <Modal.Content scrolling>
          <Form loading={loading} error={!!error}>
            <Form.Field required>
              <label>White label</label>
              <Select
                name="whiteLabelUid"
                placeholder="Select a white label"
                options={whiteLabelOptions}
                value={whiteLabelUid}
                onChange={(e, data) => this.onChange(e, { name: 'whiteLabelUid', value: data.value })}
                search
              />
            </Form.Field>
            <Form.Field>
              <Checkbox label="Premium" name="isPremium" checked={isPremium} onChange={(e, data) => this.onChange(e, { name: 'isPremium', checked: data.checked })}/>
            </Form.Field>

            <Header as="h4">Bot persona</Header>
            <Form.Group widths="equal">
              <Form.Field required>
                <label>Username</label>
                <input name="user0Username" value={user0Username} onChange={(e) => this.onChange(e, { name: 'user0Username', value: e.target.value })}/>
              </Form.Field>
              <Form.Field required>
                <label>Location</label>
                <input name="user0Location" value={user0Location} onChange={(e) => this.onChange(e, { name: 'user0Location', value: e.target.value })}/>
              </Form.Field>
              <Form.Field required>
                <label>Age</label>
                <input name="user0Age" type="number" value={user0Age} onChange={(e) => this.onChange(e, { name: 'user0Age', value: e.target.value })}/>
              </Form.Field>
            </Form.Group>
            <Form.Field required>
              <label>Description</label>
              <input name="user0Description" value={user0Description} onChange={(e) => this.onChange(e, { name: 'user0Description', value: e.target.value })}/>
            </Form.Field>
            {this.renderProfileExtras('user0', false)}

            <Header as="h4">Simulated real user</Header>
            <Form.Group widths="equal">
              <Form.Field required>
                <label>Username</label>
                <input name="user1Username" value={user1Username} onChange={(e) => this.onChange(e, { name: 'user1Username', value: e.target.value })}/>
              </Form.Field>
              <Form.Field required>
                <label>Location</label>
                <input name="user1Location" value={user1Location} onChange={(e) => this.onChange(e, { name: 'user1Location', value: e.target.value })}/>
              </Form.Field>
              <Form.Field required>
                <label>Age</label>
                <input name="user1Age" type="number" value={user1Age} onChange={(e) => this.onChange(e, { name: 'user1Age', value: e.target.value })}/>
              </Form.Field>
            </Form.Group>
            <Form.Field required>
              <label>Description</label>
              <input name="user1Description" value={user1Description} onChange={(e) => this.onChange(e, { name: 'user1Description', value: e.target.value })}/>
            </Form.Field>
            {this.renderProfileExtras('user1', true)}

            <Header as="h4">First message</Header>
            <Form.Field>
              <label>Initial message (sent by the simulated user)</label>
              <input name="message" value={message} disabled={followUp} onChange={(e) => this.onChange(e, { name: 'message', value: e.target.value })}/>
            </Form.Field>
            <Form.Field>
              <Checkbox label="Follow up (nudge the bot without a first message)" checked={followUp} onChange={(e, data) => this.onChange(e, { name: 'followUp', checked: data.checked })}/>
            </Form.Field>

            {error && <div style={{ color: '#9f3a38', marginBottom: '1em' }}>{error}</div>}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.handleClose}>Cancel</Button>
          <Button primary loading={loading} onClick={this.submit}>Create</Button>
        </Modal.Actions>
      </Modal>
    )
  }
}

export default observer(OcBotTestCreateModal)
